import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { faqs } from "../../data/content";
import { Plus, Minus } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const FAQ = () => {
  const { isAr, t } = useLang();
  const [open, setOpen] = useState(0);

  return (
    <section
      className="relative bg-bg-ivory py-[120px] md:py-[180px]"
      data-testid="faq-section"
    >
      <div className="max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex justify-between items-center text-brand-green/65 mb-16">
          <span className="page-meta">— Common Questions</span>
          <span className="page-meta">{t("الأسئلة الشائعة", "Frequently Asked")}</span>
        </div>

        <div className="grid grid-cols-12 gap-x-12 gap-y-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease }}
            className="col-span-12 md:col-span-5"
          >
            <div className="md:sticky md:top-[120px]">
              <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/55 mb-5`}>
                {t("[ الأسئلة الشائعة ]", "[ FAQ ]")}
              </div>
              <h3
                className={`${isAr ? "arabic-title" : "serif-title"} text-brand-green mb-10`}
                style={{ fontSize: isAr ? "clamp(38px, 4.6vw, 64px)" : "clamp(44px, 5vw, 92px)" }}
              >
                {isAr ? "أسئلة شائعة" : (<>Frequently<br /><em className="font-light">Asked</em></>)}
              </h3>
              {isAr ? (
                <p className="arabic-title text-brand-green/70 max-w-sm" dir="rtl" style={{ fontSize: 15, lineHeight: 1.95, fontWeight: 400 }}>
                  الوضوح يصنع الثقة. اطّلع على الإجابات أدناه أو تواصل معنا لمناقشة مشروعك مباشرة.
                </p>
              ) : (
                <p className="text-brand-green/70 max-w-sm" style={{ fontSize: 15, lineHeight: 1.8 }}>
                  Clarity brings confidence. Find answers below — or reach out to discuss your project directly.
                </p>
              )}
              <a
                href="#contact"
                className={`${isAr ? "label-meta-ar" : "label-meta"} mt-10 inline-flex items-center gap-3 text-brand-green border-b border-brand-green pb-2 hover:gap-5 transition-all duration-500 cursor-hover`}
                data-testid="faq-cta"
              >
                {t("لنتحدث", "Let's talk")}
                <span>→</span>
              </a>
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-7">
            <div className="editorial-rule mb-2" />
            {faqs.map((f, idx) => {
              const isOpen = open === idx;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.9, ease, delay: idx * 0.05 }}
                  className="border-b border-brand-green/15"
                  data-testid={`faq-${idx}`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : idx)}
                    className="w-full py-7 flex items-start justify-between gap-6 text-start cursor-hover group"
                  >
                    <div
                      className={`flex-1 ${isAr ? "arabic-title text-right" : "serif-title italic font-light"} text-brand-green`}
                      style={{ fontSize: isAr ? 19 : 23, lineHeight: 1.4 }}
                      dir={isAr ? "rtl" : "ltr"}
                    >
                      {isAr ? f.ar_q : f.en_q}
                    </div>
                    <div className="w-10 h-10 rounded-full border border-brand-green/30 flex items-center justify-center mt-1 group-hover:bg-brand-green group-hover:text-bg-ivory transition-all duration-500 flex-shrink-0">
                      {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.7, ease }}
                        className="overflow-hidden"
                      >
                        <div className="pb-8 pe-12">
                          <p
                            className={`${isAr ? "arabic-title text-right" : ""} text-brand-green/80 max-w-2xl`}
                            dir={isAr ? "rtl" : "ltr"}
                            style={{ fontSize: 15.5, lineHeight: isAr ? 1.95 : 1.8, fontWeight: 400 }}
                          >
                            {isAr ? f.ar_a : f.en_a}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
